import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from 'aws-amplify';
import { from, Observable, of, switchMap } from 'rxjs';
import { Logger } from 'src/app/common/utility/logger/logger';
import { Strings } from 'src/app/common/utility/string/string';
import { Api } from '../../http/api-endpoints';
import { HttpService } from '../../http/http.service';
import { Endpoint } from '../../http/interface/endpoint';
import { Route } from '../../http/routes';
import { StorageService } from '../../storage/storage.service';
import { Configuration } from '../classes/configuration';
import { PasswordPolicy } from '../classes/password-policy';
import { PolicyData } from '../classes/policy-data';
import { CognitoUser } from './model/CognitoUser';
import { PoolResult } from './model/pool-result';

@Injectable({ providedIn: 'root' })
export class CognitoService {

  private _poolId: string = Strings.Empty
  private _clientId: string = Strings.Empty
  private _passwordPolicy?: PasswordPolicy

  private cognitoUser: CognitoUser | undefined

  private configureAmplify(pool: string, clientId?: string): void {
    Configuration.applyCognito(pool, clientId)
  }

  public get PoolId(): string {
    this._poolId = !!this._poolId ? this._poolId : StorageService.PoolId ?? Strings.Empty
    return this._poolId
  }

  public get PasswordPolicy(): PasswordPolicy | undefined {
    return this._passwordPolicy
  }

  public set PasswordPolicy(value: PasswordPolicy | undefined) {
    this._passwordPolicy = value
  }

  public get ClientId(): string {
    this._clientId = !!this._clientId ? this._clientId : StorageService.ClientId ?? Strings.Empty
    return this._clientId
  }

  private set CognitoUser(value: CognitoUser) {
    this.cognitoUser = value
    StorageService.CognitoUser = value
  }

  public getPoolInfo(companyId: string): Observable<any> {
    const action: string = Route.Authentication.PoolId;
    const endpoint: Endpoint = Api.getEndpoint(Api.cognitoPool, action);
    return this.httpService.request(Api.cognitoPool, action, Api.createParameters(endpoint.route, [companyId]))
  }

  public getPasswordPolicy(poolId: string): Observable<PolicyData> {
    const action: string = Route.Authentication.Policy;
    const endpoint: Endpoint = Api.getEndpoint(Api.cognitoPool, action);
    return this.httpService.request(Api.cognitoPool, action, Api.createParameters(endpoint.route, [poolId]))
  }

  public signIn(username: string, password: string, companyId: string): Observable<any> {
    Logger.debug(`Authenticating user ${username} for company: ${companyId}`);

    return this.getPoolInfo(companyId)
      .pipe(
        switchMap((poolInfo: PoolResult) => {
          Logger.debug(`Authenticating user ${username}, Company ${companyId}, Cognito pool: ${poolInfo.poolId}, Client ID: ${poolInfo.clientId}`);
          this.configureAmplify(poolInfo.poolId, poolInfo.clientId);

          const result = Auth.signIn(username, password)
          result.then((cognitoUser: CognitoUser) => {
            this.CognitoUser = cognitoUser
          }).catch(error => {
            Logger.debug(`Error: ${error}`)
          })

          return from(result)
        })
      )
  }

  //TODO: Refactor these to Observables like method: signIn
  public async resendConfirmationCode(username: string): Promise<any> {
    let returnValue: any
    try {
      returnValue = await Auth.resendSignUp(username);
      Logger.debug('code resent successfully')

    } catch (err) {
      Logger.error(`error resending code: ${err}`)
    }
    return returnValue;
  }

  public async signOut(): Promise<any> {
    let returnValue: any
    try {
      await Auth.signOut({ global: true });
    } catch (error) {
      Logger.error(`error signing out: ${error}`)
    }
    return returnValue;
  }

  public async signUp(username: string, password: string, email: string, phone: string): Promise<any> {
    let returnValue: any
    try {
      returnValue = await Auth.signUp({
        username,
        password,
        attributes: {
          email,
          phone,   // E.164 format
        }
      });
    } catch (error) {
      Logger.error(`error signing up: ${error}`)
    }
    return returnValue;
  }

  public changePassword(oldPassword: string, newPassword: string): Observable<any> {
    this.configureAmplify(this.PoolId, this.ClientId);
    return of(Auth.changePassword(this.cognitoUser, oldPassword, newPassword))
  }

  public poolIdPasswordVerify(username: string, poolId?: string, clientId?: string): Observable<any> {
    Logger.debug(`Getting password reset email for ${username} for PoolId: ${poolId}`);
    return this.requestForgotPassword(username, poolId, clientId)
  }

  constructor(private httpService: HttpService) { }

  private requestForgotPassword(username: string, poolId?: string, clientId?: string): Observable<any> {
    this.configureAmplify(poolId ?? this.PoolId, clientId ?? this.ClientId);
    return from(
      Auth.forgotPassword(username)
    )
  }

  public forgotPasswordSubmit(username: string, authorizationCode: string, newPassword: string, poolId?: string, clientId?: string): Observable<any> {
    poolId = !!poolId ? poolId : this.PoolId
    clientId = !!clientId ? clientId : this.ClientId

    this.configureAmplify(poolId ?? this.PoolId, clientId ?? this.ClientId);
    return from(
      Auth.forgotPasswordSubmit(username, authorizationCode, newPassword)
    )
  }

  public changeFirstPassword(cognitoUser: any, newPassword: string, poolId?: string, clientId?: string): Observable<any> {
    this.configureAmplify(poolId ?? this.PoolId, clientId ?? this.ClientId);
    const requiredAttributes = {
      'locale': cognitoUser?.challengeParam?.userAttributes['locale']
    }

    return from(
      Auth.completeNewPassword(cognitoUser, newPassword, requiredAttributes)
    )
  }

  public resendTemporaryPassword(username: string, poolId?: string): Observable<any> {
    poolId = poolId ?? this.PoolId
    const action: string = Route.User.ResendPassword
    const endpoint: Endpoint = Api.getEndpoint(Api.user, action);
    return this.httpService.request(Api.user, action, Api.createParameters(endpoint.route, [username]), undefined, this.createPoolIdHeader(poolId))
  }

  private createPoolIdHeader(poolId?: string): HttpHeaders | undefined {
    poolId = poolId ?? this.PoolId
    return !!poolId ? new HttpHeaders({ poolId: poolId }) : undefined
  }

}
